import { Check, ContentCopy } from "@mui/icons-material";
import { Chip, IconButton, LinearProgress, Tooltip } from "@mui/material";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";
import { Section } from "components/disw";
import { ErrorBox } from "components/error";
import { useTenant } from "features/shell";
import { useState } from "react";
import useUserProfile from "./hooks/useUserProfile";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  card: {
    backgroundColor: "#ffffff",
    border: "1px solid #00000026",
    borderRadius: 3,
    padding: theme.spacing(2),
    gap: theme.spacing(3),
    boxShadow: "0px 1px 2px 0px #0000004D",
    display: "flex",
    flexDirection: "column",
    width: 667
  },
  propertyGrid: {
    minWidth: 112,
    maxWidth: 112,
    width: 112,
    flexBasis: 112,
    flexShrink: 0,
    flexGrow: 0,
    paddingRight: 8
  },
  property: {
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "16px",
    color: "#1E1E1E"
  },
  value: {
    fontFamily: "Segoe UI",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px",
    color: "#464646",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1)
  },
  roleChip: {
    backgroundColor: "#3AA9AE",
    color: "#ffffff",
    height: 22,
    "& .MuiChip-label": {
      fontFamily: "Segoe UI",
      fontSize: "11px",
      fontWeight: 600,
      paddingLeft: 10,
      paddingRight: 10
    }
  },
  permissionChip: {
    backgroundColor: "#f0f0f0",
    color: "#464646",
    height: 22,
    "& .MuiChip-label": {
      fontFamily: "Segoe UI",
      fontSize: "11px",
      fontWeight: 400,
      paddingLeft: 10,
      paddingRight: 10
    }
  },
  emptyText: {
    fontFamily: "Segoe UI",
    fontSize: "12px",
    color: "#B1B1B1"
  },
  usernameValue: {
    fontFamily: "'Consolas', 'Courier New', monospace",
    fontWeight: 400,
    fontSize: "11px",
    lineHeight: "16px",
    color: "#666666",
    backgroundColor: "#f5f5f5",
    border: "1px solid #e0e0e0",
    borderRadius: 3,
    padding: "1px 5px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    display: "inline-block",
    maxWidth: "100%"
  },
  usernameRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    overflow: "hidden"
  },
  copyButton: {
    padding: 2
  },
  copyIcon: {
    fontSize: "13px !important"
  }
}));

function PropertyRow({ label, value }: { label: string; value: string }) {
  const classes = useStyles({});
  return (
    <Grid container columnSpacing={2} wrap="nowrap">
      <Grid item className={classes.propertyGrid}>
        <Typography className={classes.property}>{label}</Typography>
      </Grid>
      <Grid item xs>
        <Typography className={classes.value}>{value}</Typography>
      </Grid>
    </Grid>
  );
}

function UsernameRow({ label, value }: { label: string; value: string }) {
  const classes = useStyles({});
  const [copied, setCopied] = useState(false);
  return (
    <Grid container columnSpacing={2} wrap="nowrap">
      <Grid item className={classes.propertyGrid}>
        <Typography className={classes.property}>{label}</Typography>
      </Grid>
      <Grid item xs zeroMinWidth>
        <div className={classes.usernameRow}>
          <Tooltip title={value} placement="top">
            <span className={classes.usernameValue}>{value}</span>
          </Tooltip>
          <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
            <IconButton
              size="small"
              className={classes.copyButton}
              data-testid="copy-username-button"
              onClick={() => {
                navigator.clipboard.writeText(value);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? (
                <Check className={classes.copyIcon} />
              ) : (
                <ContentCopy className={classes.copyIcon} />
              )}
            </IconButton>
          </Tooltip>
        </div>
      </Grid>
    </Grid>
  );
}

export default function UserProfile() {
  const classes = useStyles({});
  const { tenantKey } = useTenant();
  const { data, loading, error } = useUserProfile();

  if (loading) return <LinearProgress />;
  if (error) return <ErrorBox apolloError={error} />;

  const firstName = data?.firstName ?? "";
  const lastName = data?.lastName ?? "";
  const email = data?.email ?? data?.name ?? "";
  const userName = data?.name ?? "";
  const showUserName = userName !== "" && userName !== email;
  const roles = data?.roles ?? [];
  const permissions = data?.permissions ?? [];

  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <Section name="Account">
          <Grid container rowSpacing={0.5}>
            <Grid item xs={12}>
              <PropertyRow label="First name" value={firstName} />
            </Grid>
            <Grid item xs={12}>
              <PropertyRow label="Last name" value={lastName} />
            </Grid>
            <Grid item xs={12}>
              <PropertyRow label="Email" value={email} />
            </Grid>
            {showUserName && (
              <Grid item xs={12}>
                <UsernameRow label="SSO ID" value={userName} />
              </Grid>
            )}
            <Grid item xs={12}>
              <PropertyRow label="Tenant" value={tenantKey} />
            </Grid>
          </Grid>
        </Section>

        <Section name="Roles">
          {roles.length === 0 ? (
            <Typography
              className={classes.emptyText}
              data-testid="no-roles-text"
            >
              No roles assigned
            </Typography>
          ) : (
            <div className={classes.chipRow} data-testid="roles-list">
              {roles.map(role => (
                <Chip
                  key={role}
                  label={role}
                  className={classes.roleChip}
                  data-testid={`role-chip-${role}`}
                />
              ))}
            </div>
          )}
        </Section>

        <Section name="Permissions">
          {permissions.length === 0 ? (
            <Typography
              className={classes.emptyText}
              data-testid="no-permissions-text"
            >
              No permissions assigned
            </Typography>
          ) : (
            <div className={classes.chipRow} data-testid="permissions-list">
              {permissions.map(permission => (
                <Chip
                  key={permission}
                  label={permission}
                  className={classes.permissionChip}
                />
              ))}
            </div>
          )}
        </Section>
      </Card>
    </div>
  );
}
